import { useMemo, useState } from "react";
import { useComments, useAddComment } from "../../hooks/useTasks";
import { useUsers, userLabel } from "../../hooks/useDirectory";
import { displayName } from "../../api/tawa";
import { Avatar } from "../Avatar";
import { formatDay, isoToDayKey } from "../../lib/date";

/** Fil de discussion d'une tâche (/{id}/comments). @mention insérée en texte
 * (@Prénom Nom) — le backend gère les mentions présentes dans le body. */
export function CommentsSection({ taskId }: { taskId: string }) {
  const comments = useComments(taskId);
  const users = useUsers();
  const add = useAddComment();
  const [draft, setDraft] = useState("");

  // Dernier token « @query » en cours de frappe (pas encore clos par espace).
  const mentionQuery = useMemo(() => {
    const m = /(?:^|\s)@([\p{L}\p{N}._-]*)$/u.exec(draft);
    return m ? m[1]!.toLowerCase() : null;
  }, [draft]);

  const suggestions = useMemo(() => {
    if (mentionQuery === null) return [];
    return (users.data ?? [])
      .map((u) => displayName(u))
      .filter((n) => n.toLowerCase().includes(mentionQuery))
      .slice(0, 5);
  }, [mentionQuery, users.data]);

  const pickMention = (name: string) => {
    setDraft(draft.replace(/@[\p{L}\p{N}._-]*$/u, `@${name} `));
  };

  const submit = () => {
    const v = draft.trim();
    if (!v) return;
    add.mutate({ taskId, body: v });
    setDraft("");
  };

  return (
    <div className="detail-section">
      <div className="detail-section-title">Commentaires</div>
      {(comments.data ?? []).map((c) => {
        const who = userLabel(
          users.data,
          c.author_actor_ref || c.author_user_ref,
        );
        return (
          <div key={c.id} style={{ display: "flex", gap: 8, marginTop: 10 }}>
            <Avatar name={who} size={24} />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 12 }}>
                <span style={{ fontWeight: 600 }}>{who}</span>
                <span
                  style={{
                    fontSize: 10.5,
                    color: "var(--ink-ghost)",
                    marginLeft: 6,
                  }}
                >
                  {formatDay(isoToDayKey(c.created_at))}
                </span>
              </div>
              <div style={{ fontSize: 12.5, whiteSpace: "pre-wrap" }}>
                {c.body}
              </div>
            </div>
          </div>
        );
      })}
      {(comments.data ?? []).length === 0 && (
        <div style={{ fontSize: 11.5, color: "var(--ink-ghost)" }}>
          Aucun commentaire.
        </div>
      )}

      <div style={{ marginTop: 10, position: "relative" }}>
        <textarea
          className="comment-input"
          rows={2}
          value={draft}
          placeholder="Écrire un commentaire… @ pour mentionner (⌘/Ctrl+Entrée)"
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) =>
            (e.metaKey || e.ctrlKey) && e.key === "Enter" && submit()
          }
          aria-label="Nouveau commentaire"
        />
        {suggestions.length > 0 && (
          <div className="typeahead-list" role="listbox">
            {suggestions.map((n) => (
              <button
                key={n}
                type="button"
                role="option"
                aria-selected={false}
                className="typeahead-item"
                onMouseDown={(e) => {
                  e.preventDefault();
                  pickMention(n);
                }}
              >
                <span className="typeahead-user">
                  <Avatar name={n} size={20} />
                  <span>{n}</span>
                </span>
              </button>
            ))}
          </div>
        )}
        <div
          style={{ display: "flex", justifyContent: "flex-end", marginTop: 4 }}
        >
          <button className="btn" onClick={submit} disabled={!draft.trim()}>
            Commenter
          </button>
        </div>
      </div>
    </div>
  );
}
